import React, { useState, useMemo } from "react";
import { useGetActivityMarketingDetailInfinite } from "api/showing-activity/query";
import { useInfiniteScroll } from "hooks";

// Assets
import IconXSrc from "assets/x-black.svg";
import DefaultImage from "assets/empty-listing-photo.png";

// Components
import CModal from "components/v1/modal";

const ModalDetailMarketing = ({ onClose, marketing, ...props }) => {
  const [limit] = useState(10);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [imageAspectRatio, setImageAspectRatio] = useState("aspect-[327/184]");

  const handleImageLoad = (e) => {
    const img = e.target;
    const width = img.naturalWidth;
    const height = img.naturalHeight;

    if (width === height) {
      setImageAspectRatio("aspect-square");
    } else if (height > width) {
      setImageAspectRatio("aspect-[184/327]");
    } else {
      setImageAspectRatio("aspect-[327/184]");
    }
  };

  const params = {
    _limit: limit,
  };

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetActivityMarketingDetailInfinite({
      id: marketing?.id,
      params,
      enabled: !!marketing?.id,
    });

  const activities = useMemo(() => {
    return data?.pages?.flatMap((page) => page?.data?.data || []) || [];
  }, [data]);

  const currentActivity = selectedActivity || activities[0];

  const observerTarget = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <CModal onClose={onClose} {...props}>
      <div className="w-[850px] rounded-md">
        <div className="py-2 px-6 flex items-center justify-between border-b border-solid border-[#D8D8D9]">
          <span className="text-[17px] font-inter-semibold text-[#252526]">
            Riwayat Showing: {marketing?.user?.full_name || ""}
          </span>
          <img
            src={IconXSrc}
            alt="Close"
            onClick={onClose}
            className="cursor-pointer w-[14px] h-[14px] object-contain"
          />
        </div>

        {isLoading ? (
          <div className="py-8 px-4 text-center">
            <p className="text-[13px] font-inter text-[#808285]">Loading...</p>
          </div>
        ) : (
          <div className="flex items-start h-full">
            <div className="flex-1 border-r border-solid border-[#DEDFDE] h-[501px] flex flex-col">
              <div className="flex-1 overflow-y-auto overflow-hidden">
                {activities.map((activity, index) => (
                  <div
                    key={`activity-${activity?.id || index}`}
                    className={`flex items-center gap-[12px] py-2 px-4 border-b-[0.5px] border-solid border-[#DEDFDE] cursor-pointer hover:bg-[#FAFAFA] ${
                      currentActivity?.id === activity?.id ? "bg-[#F0F0F0]" : ""
                    }`}
                    onClick={() => setSelectedActivity(activity)}
                  >
                    <img
                      src={activity?.property?.image ?? DefaultImage}
                      alt={activity?.property?.title}
                      className="rounded-[4px] w-[87px] aspect-square object-cover"
                    />

                    <div>
                      <div className="flex items-center gap-[12px] mb-2">
                        <p className="text-[11px] font-inter text-[#686A68]">
                          {activity?.visit_date || "-"}
                        </p>

                        {activity?.is_cobroke && (
                          <span className="flex items-center justify-center h-4 px-1 bg-[#EBF0F7] rounded-[2px] text-[11px] font-inter-semibold text-[#3268AE]">
                            Co Broking
                          </span>
                        )}
                      </div>
                      {activity?.is_marketing_buyer ? (
                        <>
                          <p className="text-[11px] font-inter text-[#686A68]">
                            Alamat Property
                          </p>
                          <p className="text-[13px] font-inter text-[#252526]">
                            {activity?.property?.street || "-"}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="line-clamp-2 text-[13px] font-inter text-[#252526]">
                            {activity?.property?.title || "-"}
                          </p>
                          <p className="text-[13px] font-inter text-[#686A68]">
                            {activity?.property?.area || ""}
                            {activity?.property?.area &&
                            activity?.property?.city
                              ? ", "
                              : ""}
                            {activity?.property?.city || ""}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                ))}

                {isFetchingNextPage && (
                  <div className="py-4 text-center">
                    <p className="text-[11px] font-inter text-[#808285]">
                      Loading...
                    </p>
                  </div>
                )}

                {!hasNextPage && activities.length > 0 && (
                  <div className="py-4 text-center">
                    <p className="text-[11px] font-inter text-[#808285]">
                      Tidak ada data lagi
                    </p>
                  </div>
                )}

                <div ref={observerTarget} style={{ height: "20px" }} />
              </div>
            </div>
            <div className="flex-1 h-[545px] overflow-y-auto">
              {currentActivity ? (
                <>
                  <div className="py-2 px-4 border-b-[0.5px] border-dashed border-[#DEDFDE] flex flex-col gap-1">
                    <p className="text-[17px] font-inter-semibold text-[#686A68] leading-[27.5px]">
                      Informasi kunjungan
                    </p>
                    <p className="text-[11px] font-inter text-[#686A68]">
                      Tanggal Kunjungan
                    </p>
                    <p className="text-[13px] font-inter text-[#252526]">
                      {currentActivity?.visit_date || "-"}
                    </p>
                    {!currentActivity?.is_marketing_buyer && (
                      <>
                        <p className="text-[11px] font-inter text-[#686A68]">
                          Alamat Property
                        </p>
                        <p className="text-[13px] font-inter text-[#252526]">
                          {currentActivity?.property?.street || "-"}
                        </p>
                      </>
                    )}
                  </div>
                  <div className="py-2 px-4 border-b-[0.5px] border-dashed border-[#DEDFDE] flex flex-col gap-0.5">
                    <p className="text-[17px] font-inter-semibold text-[#686A68] leading-[27.5px] mb-1">
                      Prospek Pembeli/Penyewa
                    </p>
                    <p className="text-[11px] font-inter text-[#686A68]">
                      Nama:
                    </p>
                    <p className="text-[13px] font-inter text-[#252526] mb-1">
                      {currentActivity?.client?.full_name || "-"}
                    </p>
                    <p className="text-[11px] font-inter text-[#686A68]">
                      Tanggapan:
                    </p>
                    <p className="text-[13px] font-inter text-[#252526]">
                      {currentActivity?.client?.response || "-"}
                    </p>
                  </div>
                  <div className="py-2 px-4 mb-2">
                    <p className="text-[17px] font-inter-semibold text-[#686A68] leading-[27.5px] mb-2">
                      Hasil Kunjungan
                    </p>
                    {!!currentActivity?.visit_image && (
                      <img
                        src={currentActivity?.visit_image}
                        alt="Hasil Kunjungan"
                        className={`rounded-[4px] w-full ${imageAspectRatio} object-cover`}
                        onLoad={handleImageLoad}
                      />
                    )}
                  </div>
                </>
              ) : (
                <div className="py-8 px-4 text-center">
                  <p className="text-[13px] font-inter text-[#808285]">
                    Pilih aktivitas untuk melihat detail
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </CModal>
  );
};

export default function Wrapper({ isOpen, marketing, ...props }) {
  if (!isOpen) return null;
  return (
    <ModalDetailMarketing isOpen={isOpen} marketing={marketing} {...props} />
  );
}
